import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import TerminalIcon from '@mui/icons-material/Terminal';
import BlockIcon from '@mui/icons-material/Block';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import TokenContext from './TokenContext';
import { approveMcpClient, describeMcpRequest } from '../api/OAuthApi';

export const PENDING_AUTHORIZE_KEY = 'mcpAuthorizeRequest';

const PERMISSIONS = [
    'Ver y editar banners y carruseles con tu usuario',
    'Crear widgets nuevos, que nacen apagados hasta que los enciendas',
    'Consultar el historial de cambios y deshacerlos',
];

const emailFromToken = (jwt) => {
    try {
        return JSON.parse(atob(jwt.split('.')[1])).sub || '';
    } catch (e) {
        return '';
    }
};

// "Claude Code (consumer-configs-mcp)" -> name "Claude Code", detail "consumer-configs-mcp"
const splitClientName = (name) => {
    const match = /^(.*?)\s*\((.+)\)\s*$/.exec(name || '');
    return match ? { name: match[1], detail: match[2] } : { name: name || 'Un asistente', detail: '' };
};

const tileSx = {
    width: 64,
    height: 64,
    borderRadius: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
    boxShadow: '0 1px 2px rgba(16,18,27,0.08), 0 8px 24px -12px rgba(16,18,27,0.35)',
};

const Permission = ({ icon, children, muted, delay = 0 }) => (
    <Box component="li" className="rise" sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', animationDelay: `${delay}s !important` }}>
        <Box sx={{
            width: 22, height: 22, borderRadius: '50%', flex: 'none', mt: '1px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: muted ? '#eceef1' : '#e6f7ef', color: muted ? '#8a8f9b' : '#00A86C',
        }}>
            {icon}
        </Box>
        <Typography sx={{ fontSize: 14, lineHeight: '22px', color: muted ? '#6b6f7b' : '#1d2230' }}>{children}</Typography>
    </Box>
);

function Authorize() {
    const { token, setToken } = useContext(TokenContext);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const request = new URLSearchParams(window.location.search).get('request');
    const jwt = token || localStorage.getItem('token');
    const email = emailFromToken(jwt || '');

    useEffect(() => {
        if (!request) {
            navigate('/banners');
            return;
        }
        if (!jwt) {
            sessionStorage.setItem(PENDING_AUTHORIZE_KEY, request);
            navigate('/');
            return;
        }
        describeMcpRequest(request)
            .then(setClient)
            .catch(() => {
                enqueueSnackbar('La solicitud de conexión no es válida o expiró. Vuelve a iniciarla desde tu asistente.', { variant: 'error' });
                navigate('/banners');
            });
    }, [request, jwt, navigate, enqueueSnackbar]);

    const switchAccount = () => {
        sessionStorage.setItem(PENDING_AUTHORIZE_KEY, request);
        setToken(null);
        navigate('/');
    };

    const handleApprove = async () => {
        setLoading(true);
        try {
            const { redirect_uri: redirectUri } = await approveMcpClient(request, jwt);
            setDone(true);
            setTimeout(() => window.location.assign(redirectUri), 900);
        } catch (e) {
            if (e.response && e.response.status === 401) {
                switchAccount();
                return;
            }
            enqueueSnackbar('No se pudo autorizar. Vuelve a iniciar la conexión desde tu asistente.', { variant: 'error' });
            setLoading(false);
        }
    };

    if (!request || !jwt || !client) {
        return null;
    }

    const { name: clientName, detail: clientDetail } = splitClientName(client.client_name);

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'radial-gradient(1200px 600px at 50% -10%, #ffffff 0%, #f3f2ef 55%, #ecebe7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 6,
            fontFamily: "'Poppins', 'Roboto', sans-serif",
        }}>
            <Box sx={{
                width: '100%',
                maxWidth: 420,
                '--ease': 'cubic-bezier(.2,.7,.2,1)',
                animation: 'authorize-in .5s var(--ease) both',
                '@keyframes authorize-in': { from: { opacity: 0, transform: 'translateY(12px) scale(.985)' }, to: { opacity: 1, transform: 'none' } },
                '@keyframes tile-left': { from: { opacity: 0, transform: 'translateX(-18px)' }, to: { opacity: 1, transform: 'none' } },
                '@keyframes tile-right': { from: { opacity: 0, transform: 'translateX(18px)' }, to: { opacity: 1, transform: 'none' } },
                '@keyframes link-draw': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
                '@keyframes pop': { '0%': { opacity: 0, transform: 'scale(.4)' }, '70%': { opacity: 1, transform: 'scale(1.12)' }, '100%': { transform: 'scale(1)' } },
                '@keyframes rise': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
                '@keyframes sheen': { from: { transform: 'translateX(-120%) skewX(-18deg)' }, to: { transform: 'translateX(220%) skewX(-18deg)' } },
                '& .tile-left': { animation: 'tile-left .6s var(--ease) .1s both' },
                '& .tile-right': { animation: 'tile-right .6s var(--ease) .1s both' },
                '& .link': { transformOrigin: 'left center', animation: 'link-draw .5s var(--ease) .45s both' },
                '& .link-r': { transformOrigin: 'right center' },
                '& .link-check': { animation: 'pop .45s var(--ease) .85s both' },
                '& .rise': { animation: 'rise .5s var(--ease) both' },
                '@media (prefers-reduced-motion: reduce)': { '&, & *': { animation: 'none !important', transition: 'none !important' } },
            }}>
                <Box sx={{
                    background: '#fff',
                    borderRadius: '24px',
                    px: { xs: 3, sm: 5 },
                    pt: 5,
                    pb: 4,
                    boxShadow: '0 1px 1px rgba(16,18,27,0.04), 0 24px 60px -24px rgba(16,18,27,0.25)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3.5 }}>
                        <Box className="tile-left" sx={{ ...tileSx, background: 'linear-gradient(160deg, #2a2f3d 0%, #141720 100%)', color: '#fff', position: 'relative', overflow: 'hidden', '&::after': { content: '""', position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(255,255,255,.14), rgba(255,255,255,0) 45%)' } }} aria-hidden="true">
                            <TerminalIcon sx={{ fontSize: 30, position: 'relative' }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} aria-hidden="true">
                            <Box className="link" sx={{ width: 22, height: 2, borderRadius: 2, background: 'linear-gradient(90deg, #d8d6cf, #00A86C)' }} />
                            <Box className="link-check" sx={{ width: 26, height: 26, borderRadius: '50%', background: '#00A86C', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 4px #fff, 0 6px 14px -6px rgba(0,168,108,.7)' }}>
                                <CheckIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Box className="link link-r" sx={{ width: 22, height: 2, borderRadius: 2, background: 'linear-gradient(90deg, #00A86C, #d8d6cf)' }} />
                        </Box>
                        <Box
                            className="tile-right"
                            component="img"
                            src={`${process.env.PUBLIC_URL}/logo192.png`}
                            alt="Rappi"
                            sx={{ ...tileSx, objectFit: 'cover' }}
                        />
                    </Box>

                    <Typography component="h1" sx={{
                        fontFamily: 'inherit',
                        fontWeight: 600,
                        fontSize: 24,
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        textAlign: 'center',
                        color: '#141720',
                        mb: 1,
                    }}>
                        Conectar {clientName}
                    </Typography>
                    <Typography sx={{ textAlign: 'center', color: '#6b6f7b', fontSize: 14, lineHeight: 1.5, mb: 3.5 }}>
                        {clientDetail ? (
                            <>Servidor <Box component="code" sx={{ fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontSize: 12.5, color: '#1d2230', background: '#f3f2ef', px: 0.75, py: 0.25, borderRadius: '6px' }}>{clientDetail}</Box> · </>
                        ) : null}
                        Cada cambio quedará registrado a tu nombre.
                    </Typography>

                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 2.5, mb: 3.5, display: 'grid', gap: 1.75, background: '#f8f8f6', borderRadius: '16px' }}>
                        {PERMISSIONS.map((permission, index) => (
                            <Permission key={permission} delay={0.5 + index * 0.08} icon={<CheckIcon sx={{ fontSize: 14 }} />}>{permission}</Permission>
                        ))}
                        <Permission muted delay={0.5 + PERMISSIONS.length * 0.08} icon={<BlockIcon sx={{ fontSize: 14 }} />}>Nunca podrá borrar widgets, solo apagarlos.</Permission>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, px: 0.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '50%', flex: 'none', background: '#e6f7ef', color: '#00A86C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 15 }} aria-hidden="true">
                            {(email || '?').charAt(0).toUpperCase()}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: 11, color: '#8a8f9b', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>Autorizar como</Typography>
                            <Typography sx={{ fontSize: 14, color: '#141720', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</Typography>
                        </Box>
                        <Button onClick={switchAccount} disabled={loading} size="small" sx={{ fontSize: 13, color: '#00A86C', fontWeight: 500, minWidth: 0, px: 1, fontFamily: 'inherit' }}>
                            Cambiar
                        </Button>
                    </Box>

                    <Button
                        variant="contained"
                        disableElevation
                        fullWidth
                        onClick={handleApprove}
                        disabled={loading}
                        sx={{
                            py: 1.6, fontSize: 15, fontWeight: 600, borderRadius: '14px', fontFamily: 'inherit',
                            position: 'relative', overflow: 'hidden',
                            background: done ? '#00A86C' : '#141720', color: '#fff',
                            boxShadow: done ? '0 10px 24px -10px rgba(0,168,108,.6)' : '0 10px 24px -12px rgba(20,23,32,.6)',
                            transition: 'transform .15s ease, background .25s ease, box-shadow .25s ease',
                            '&:hover': { background: done ? '#00A86C' : '#000', transform: 'translateY(-1px)' },
                            '&:active': { transform: 'translateY(0) scale(.985)' },
                            '&.Mui-disabled': { background: done ? '#00A86C' : '#c9cbd1', color: '#fff' },
                            '&::after': { content: '""', position: 'absolute', top: 0, bottom: 0, left: 0, width: '40%', background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.18), rgba(255,255,255,0))', transform: 'translateX(-120%) skewX(-18deg)' },
                            '&:hover::after': { animation: 'sheen .9s ease' },
                        }}
                    >
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            {done && <CheckIcon sx={{ fontSize: 18 }} />}
                            {done ? `Listo, volviendo a ${clientName}` : loading ? 'Autorizando…' : 'Autorizar acceso'}
                        </Box>
                    </Button>
                    <Button
                        fullWidth
                        onClick={() => navigate('/banners')}
                        disabled={loading}
                        sx={{ mt: 1, py: 1.25, color: '#6b6f7b', fontSize: 14, fontFamily: 'inherit', fontWeight: 500 }}
                    >
                        Cancelar
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 3, color: '#8a8f9b', fontSize: 12 }}>
                    <LockOutlinedIcon sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: 12, color: 'inherit' }}>Conexión segura</Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default Authorize;
